/**
 * Placeholder substitution, shared because the preview and the sender must agree.
 *
 * This lived in the server's invitation feature until the settings screen grew a preview. Two
 * implementations of "what will this template actually say" is the one arrangement guaranteed to
 * mislead somebody: an administrator tunes the wording against a preview, and the email that goes
 * out differs. Now both sides call this.
 */

/**
 * Case-insensitive, matching the C# StringComparer.OrdinalIgnoreCase replacement.
 *
 * Any placeholder naming a URL goes through unescaped: it is one this product built and it lands in
 * an href. Matched by suffix so InteractionUrl gets the same treatment as InterviewUrl - the
 * alternative was a second list to forget to update. Everything else is supplied by a candidate or
 * an administrator and is escaped.
 */
export function applyPlaceholders(
    template: string, values: Record<string, string>
): string {
    return (template ?? '').replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
        const key = Object.keys(values).find((k) => k.toLowerCase() === name.toLowerCase());

        if (key === undefined) {
            return match;
        }

        return key.toLowerCase().endsWith('url') ? values[key]! : escapeHtml(values[key]!);
    });
}

/**
 * Appends the configured signature to a body.
 *
 * One setting, applied by every sender, so branding cannot drift between an invitation and a
 * decision email. Placeholders are applied to the signature as well - it is written by an
 * administrator and typically carries the company name.
 */
export function withSignature(
    bodyHtml: string, signatureHtml: string, values: Record<string, string>
): string {
    const signature = (signatureHtml ?? '').trim();

    return signature === ''
        ? bodyHtml
        : `${bodyHtml}<div style="margin-top:24px">${applyPlaceholders(signature, values)}</div>`;
}

export const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

/**
 * What a text message costs to send, and whether it is about to be split.
 *
 * Worth knowing before a campaign rather than after: a link pushes most templates past one segment,
 * and operators bill per segment. The thresholds are the GSM standard - 160 characters in the basic
 * alphabet, 70 once anything outside it appears, and less per part when a message is split because
 * the concatenation header eats into each one.
 *
 * A survey link is plain ASCII, so what tips a message into Unicode is almost always the wording
 * around it: a Hindi template, a curly quote pasted from Word, or an emoji.
 */
export interface SmsCost {
    characters: number;
    segments: number;
    /** True when something outside the GSM basic alphabet forces 70 characters per segment. */
    unicode: boolean;
    /** How many more characters fit before another segment is charged. */
    remaining: number;
}

/**
 * The GSM 03.38 basic set, plus the extension characters that cost two.
 *
 * Anything else - a curly quote, an emoji, any Indic script - makes the whole message Unicode, not
 * just that character. That is why one pasted apostrophe can double the bill for a campaign.
 */
const GSM_BASIC =
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?'
    + '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

const GSM_EXTENDED = '^{}\\[~]|€';

export function smsCost(text: string): SmsCost {
    const message = text ?? '';
    let units = 0;
    let unicode = false;

    for (const character of message) {
        if (GSM_EXTENDED.includes(character)) {
            // An escape byte plus the character itself.
            units += 2;
        } else if (GSM_BASIC.includes(character)) {
            units += 1;
        } else {
            unicode = true;
            break;
        }
    }

    // Unicode is counted in UTF-16 code units, so an emoji outside the basic plane costs two.
    if (unicode) {
        units = message.length;
    }

    const single = unicode ? 70 : 160;
    const multi = unicode ? 67 : 153;

    if (units === 0) {
        return { characters: 0, segments: 0, unicode, remaining: single };
    }

    const segments = units <= single ? 1 : Math.ceil(units / multi);
    const capacity = segments === 1 ? single : segments * multi;

    return { characters: units, segments, unicode, remaining: capacity - units };
}
